import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganisationUsers, updateUser, removeUserFromOrganisation, resendSetupLink } from '../api';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';

function EditUserForm({ u, onSave, onCancel }) {
  const [firstName, setFirstName] = useState(u.firstName);
  const [lastName, setLastName] = useState(u.lastName);
  const [email, setEmail] = useState(u.email);
  const [isActive, setIsActive] = useState(u.isActive);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await updateUser(u.userId, { firstName, lastName, email, isActive });
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginTop: '0.5rem' }}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label>First Name</label>
        <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
      </div>
      <div className="form-group">
        <label>Last Name</label>
        <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
      </div>
      <div className="form-group">
        <label>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      <div className="form-group">
        <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ width: 'auto' }} />
          Active
        </label>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default function OrganisationUsersPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const orgIdParam = searchParams.get('orgId');
  const adminOrgs = (user?.orgAssignments || []).filter((a) => a.profileName === 'Admin');

  const [selectedOrg, setSelectedOrg] = useState(orgIdParam || '');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [resending, setResending] = useState(null);
  const [message, setMessage] = useState('');

  const loadUsers = useCallback(() => {
    if (!selectedOrg) return;
    setLoading(true);
    setError('');
    getOrganisationUsers(selectedOrg)
      .then(setUsers)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedOrg]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSave = () => {
    setEditingId(null);
    loadUsers();
  };

  const handleRemove = async (u) => {
    if (u.userId === user?.id) {
      alert('You cannot remove yourself from this organisation.');
      return;
    }
    if (!window.confirm(`Remove ${u.firstName} ${u.lastName} from this organisation?`)) return;
    try {
      await removeUserFromOrganisation(selectedOrg, u.userId);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResend = async (u) => {
    setResending(u.userId);
    setMessage('');
    try {
      await resendSetupLink(u.userId);
      setMessage(`Setup link resent to ${u.email}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(null);
    }
  };

  const totalCols = 6;

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Users' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>Organisation Users</h2>
          <Link to="/add-user" className="btn btn-primary" style={{ fontSize: '0.85rem', textDecoration: 'none' }}>Add User</Link>
        </div>

        {!orgIdParam && (
          <div className="form-group">
            <label>Organisation</label>
            <select value={selectedOrg} onChange={(e) => { setSelectedOrg(e.target.value); setUsers([]); setError(''); setEditingId(null); }}>
              <option value="">Select organisation...</option>
              {adminOrgs.map((a) => (
                <option key={a.organisationId} value={a.organisationId}>{a.organisationName}</option>
              ))}
            </select>
          </div>
        )}

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}
        {loading && <p>Loading...</p>}

        {!loading && selectedOrg && users.length > 0 && (
          <div className="table-scroll">
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Profile</th>
                <th style={{ textAlign: 'center', padding: '0.5rem', borderBottom: '2px solid #e5e7eb', width: '1%' }}>Active</th>
                <th style={{ textAlign: 'center', padding: '0.5rem', borderBottom: '2px solid #e5e7eb', width: '1%' }}>Training</th>
                <th style={{ textAlign: 'right', padding: '0.5rem', borderBottom: '2px solid #e5e7eb', width: '1%', whiteSpace: 'nowrap' }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.userId}>
                  <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }} colSpan={editingId === u.userId ? totalCols : 1}>
                    {editingId === u.userId ? (
                      <EditUserForm u={u} onSave={handleSave} onCancel={() => setEditingId(null)} />
                    ) : (
                      <>{u.firstName} {u.lastName}</>
                    )}
                  </td>
                  {editingId !== u.userId && (
                    <>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{u.email}</td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{u.profileName}</td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>{u.isActive ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}><Link to={`/training-records?userId=${u.userId}`}>View</Link></td>
                      <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                          <button className="btn btn-outline" style={{ fontSize: '0.8rem' }} onClick={() => setEditingId(u.userId)}>Edit</button>
                          <button className="btn btn-outline" style={{ fontSize: '0.8rem', color: '#dc2626' }} onClick={() => handleRemove(u)}>Remove</button>
                          {u.pendingSetup && (
                            <button
                              className="btn btn-outline"
                              style={{ fontSize: '0.8rem', color: '#4361ee' }}
                              disabled={resending === u.userId}
                              onClick={() => handleResend(u)}
                            >
                              {resending === u.userId ? 'Sending...' : 'Resend Login Link'}
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {!loading && selectedOrg && users.length === 0 && !error && <EmptyState message="No users found." />}
      </div>
    </div>
  );
}
